import { FC, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageSection from "@app/shared/PageSection";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { QESButton } from "@app/shared/qes-standards/Button";
import SelectedNetworksWidget from "@app/compliance-connect/regulator/widgets/SelectedNetworksWidget";
import ExceptionReviewStatusWidget from "@app/compliance-connect/regulator/widgets/ExceptionReviewStatusWidget";
import IssuerComplianceComparisonWidget from "@app/compliance-connect/regulator/widgets/IssuerComplianceComparisonWidget";
import NetworkCompositionWidget from "@app/compliance-connect/regulator/widgets/NetworkCompositionWidget";
import IsolatedCoverageComparisonWidget from "@app/compliance-connect/regulator/widgets/IsolatedCoverageComparisonWidget";
import GapOverlapWidget from "@app/compliance-connect/regulator/widgets/GapOverlapWidget";
import ExceptionOverlapWidget from "@app/compliance-connect/regulator/widgets/ExceptionOverlapWidget";
import { SelectedNetwork } from "@app/compliance-connect/regulator/complianceConnectTypes";

interface NetworkComparisonPageState {
    selectedNetworks: SelectedNetwork[];
}

const NetworkComparisonPage: FC = () => {
    const history = useHistory();
    const location = useLocation<NetworkComparisonPageState>();
    const selectedNetworks = location.state?.selectedNetworks ?? [];
    const selectedNetworkIds = useMemo(() => selectedNetworks.map(n => n.networkId), [selectedNetworks]);

    return (
        <Page documentTitle="Network Comparison">
            <Breadcrumbs template={BreadcrumbTemplateEnum.ComplianceConnect} items={[{ text: "Network Comparison" }]} />
            <PageInner>
                <PageHeader title="Network Comparison" disableHR={true}>
                    <PageHeaderRight>
                        <QESButton
                            qesTestId="compliance-connect__back-to-project-dashboard"
                            qesType="secondary"
                            onClick={() => history.push("/compliance-connect")}
                        >
                            Return to Project Dashboard
                        </QESButton>
                    </PageHeaderRight>
                </PageHeader>
                <PageSection className="mb-3">
                    <SelectedNetworksWidget networks={selectedNetworks} />
                </PageSection>
                <PageSection className="mb-3">
                    <div className="d-flex justify-content-end mb-2">
                        <QESButton
                            qesTestId="compliance-connect__create-export"
                            qesType="secondary"
                        >
                            Create Export
                        </QESButton>
                    </div>
                    <ExceptionReviewStatusWidget networkIds={selectedNetworkIds} />
                </PageSection>
                <PageSection className="mb-3">
                    <IssuerComplianceComparisonWidget networkIds={selectedNetworkIds} />
                </PageSection>
                <PageSection className="mb-3">
                    <div className="row">
                        <div className="col-md-6">
                            <NetworkCompositionWidget />
                        </div>
                        <div className="col-md-6">
                            <IsolatedCoverageComparisonWidget />
                        </div>
                    </div>
                </PageSection>
                <PageSection>
                    <div className="row">
                        <div className="col-md-6">
                            <GapOverlapWidget />
                        </div>
                        <div className="col-md-6">
                            <ExceptionOverlapWidget />
                        </div>
                    </div>
                </PageSection>
            </PageInner>
        </Page>
    );
};

export default NetworkComparisonPage;
